/* eslint-disable import/extensions,import/no-unresolved,import/no-duplicates,import/no-named-as-default-member */
/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import * as Raven from 'raven-js';

// Needed for redux-saga es6 generator support
import 'babel-polyfill';

// Import all the third party stuff
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'react-router-redux';
import FontFaceObserver from 'fontfaceobserver';
import 'sanitize.css/sanitize.css';

// Import root app
import App from 'containers/App';

// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider';

// Load the favicon, the manifest.json file and the .htaccess file
/* eslint-disable import/no-webpack-loader-syntax */
import '!file-loader?name=[name].[ext]!./images/favicon.ico';
import '!file-loader?name=[name].[ext]!./images/icon-72x72.png';
import '!file-loader?name=[name].[ext]!./images/icon-96x96.png';
import '!file-loader?name=[name].[ext]!./images/icon-120x120.png';
import '!file-loader?name=[name].[ext]!./images/icon-128x128.png';
import '!file-loader?name=[name].[ext]!./images/icon-144x144.png';
import '!file-loader?name=[name].[ext]!./images/icon-152x152.png';
import '!file-loader?name=[name].[ext]!./images/icon-167x167.png';
import '!file-loader?name=[name].[ext]!./images/icon-180x180.png';
import '!file-loader?name=[name].[ext]!./images/icon-192x192.png';
import '!file-loader?name=[name].[ext]!./images/icon-384x384.png';
import '!file-loader?name=[name].[ext]!./images/icon-512x512.png';
import '!file-loader?name=[name].[ext]!./images/real-logo.png';
import '!file-loader?name=[name].[ext]!./images/woolworths-logo.png';
import '!file-loader?name=[name].[ext]!./images/hollard-logo.png';
import '!file-loader?name=[name].[ext]!./images/asia-logo.png';
import '!file-loader?name=[name].[ext]!./images/kogan-logo.png';
import '!file-loader?name=[name].[ext]!./images/vodafone-logo.png';
import '!file-loader?name=[name].[ext]!./images/Trip-Cancellation-gray.png';
import '!file-loader?name=[name].[ext]!./images/Trip-Distruption-gray.png';
import '!file-loader?name=[name].[ext]!./images/Trip-Cancellation-New-h.png';
import '!file-loader?name=[name].[ext]!./images/Trip-Cancellation-gray.png';
import '!file-loader?name=[name].[ext]!./images/demo-appsure-logo.jpg';
import '!file-loader?name=[name].[ext]!./images/Medical-gray.png';
import '!file-loader?name=[name].[ext]!./images/Luggage-gray.png';
import '!file-loader?name=[name].[ext]!./images/Other-gray.png';
import '!file-loader?name=[name].[ext]!./images/Other-gray-h.png';
import '!file-loader?name=[name].[ext]!./images/Trip-Cancellation-New-h.png';
import '!file-loader?name=[name].[ext]!./images/Medical-gray-h.png';
import '!file-loader?name=[name].[ext]!./images/Trip-Cancellation-New-gray-h.png';
import '!file-loader?name=[name].[ext]!./assets/checkExpiredTimeWorker.js';
import '!file-loader?name=[name].[ext]!./manifest.json';
import '!file-loader?name=[name].[ext]!./web.config';
import 'file-loader?name=[name].[ext]!./.htaccess'; // eslint-disable-line import/extensions
import '!file-loader?name=[name].[ext]!./images/world2cover-logo.png';
/* eslint-enable import/no-webpack-loader-syntax */

import configureStore from './configureStore';
import Config from './config';

// Import i18n messages
import { translationMessages } from './i18n';

// Import CSS reset and Global Styles
import './global-styles';

import history from './utils/routeHistory';
import './assets/style.css';

const runtime = require('offline-plugin/runtime');

// Observe loading of Open Sans (to remove open sans, remove the <link> tag in
// the index.html file and this observer)
const openSansObserver = new FontFaceObserver('Open Sans', {});

// When Open Sans is loaded, add a font-family using Open Sans to the body
openSansObserver.load().then(() => {
  document.body.classList.add('fontLoaded');
}, () => {
  document.body.classList.remove('fontLoaded');
});

// Create redux store with history
const initialState = {};
const store = configureStore(initialState, history);
const MOUNT_NODE = document.getElementById('app');

const render = (messages) => {
  ReactDOM.render(
    <Provider store={store}>
      <LanguageProvider messages={messages}>
        <ConnectedRouter history={history}>
          <App />
        </ConnectedRouter>
      </LanguageProvider>
    </Provider>,
    MOUNT_NODE
  );
};

// Sentry
if (process.env.NODE_ENV === 'production') {
  Raven
    .config('https://5b118b87e45445d9ba98c3a3766d64ab@sentry.io/275196', {
      release: Config.version,
      environment: process.env.NODE_ENV,
      autoBreadcrumbs: {
        xhr: false,      // XMLHttpRequest
        console: false,  // console logging
        dom: true,       // DOM interactions, i.e. clicks/typing
        location: true, // url changes, including pushState/popState
        sentry: true,     // sentry events
      },
    })
    .install();
}

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['./i18n', 'containers/App'], () => {
    ReactDOM.unmountComponentAtNode(MOUNT_NODE);
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  (new Promise((resolve) => {
    resolve(import('intl'));
  }))
    .then(() => Promise.all([
      import('intl/locale-data/jsonp/en.js'),
      import('intl/locale-data/jsonp/de.js'),
    ]))
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
if (process.env.NODE_ENV === 'production') {
  runtime.install({
    onUpdateReady: () => {
      // Tells to new SW to take control immediately
      runtime.applyUpdate();
    },
    onUpdated: () => {
      // Reload the webpage to load into the new version
      window.location.reload();
    },
  }); // eslint-disable-line global-require
}



// WEBPACK FOOTER //
// ./app/app.js